import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Tag } from "@/components/Layout";
import { Plane, Trophy } from "lucide-react";

/* Team-visible leaderboards — three small lists side-by-side. */
function Avatar({ src, name, size = 28 }) {
  return src ? (
    <img src={src} alt="" className="rounded-full object-cover bg-slate-200" style={{ width: size, height: size }} />
  ) : (
    <div className="rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600" style={{ width: size, height: size }}>
      {(name || "?").slice(0, 1).toUpperCase()}
    </div>
  );
}

function Row({ avatar, name, title, right, rightTone = "slate", testid }) {
  return (
    <li className="flex items-center gap-2.5 py-1.5" data-testid={testid}>
      <Avatar src={avatar} name={name} />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-slate-800 truncate">{name}</div>
        {title && <div className="text-[10px] text-slate-500 truncate">{title}</div>}
      </div>
      <Tag tone={rightTone}>{right}</Tag>
    </li>
  );
}

const tone = (i) => ["red", "orange", "yellow", "slate", "slate"][i] || "slate";

export default function TeamLeaderboards() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/dashboard/leaderboards")
       .then((r) => { if (alive) setData(r.data); })
       .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Couldn't load"); });
    return () => { alive = false; };
  }, []);

  if (err) return <div className="card-flat p-4 text-xs text-slate-500" data-testid="leaderboards-err">{err}</div>;
  if (!data) return <div className="card-flat p-4 text-xs text-slate-400">Loading leaderboards…</div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-testid="team-leaderboards">
      {/* Worst leave balances */}
      <div className="card-flat overflow-hidden" data-testid="lb-worst-leave">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between bg-orange-50">
          <div className="flex items-center gap-1.5">
            <Plane size={13} className="text-orange-600" />
            <div className="font-display text-sm font-bold text-slate-900">Most leave consumed</div>
          </div>
          <span className="text-[10px] text-slate-500">{data.as_of}</span>
        </div>
        <ul className="px-3 py-2">
          {data.worst_leave.length === 0 && <li className="text-xs text-slate-400 px-1 py-3">No approved leave yet this year.</li>}
          {data.worst_leave.map((u, i) => (
            <Row key={u.user_id} avatar={u.avatar} name={u.name} title={u.title}
                 right={`${u.days_taken}d`} rightTone={tone(i)} testid={`lb-leave-${i}`} />
          ))}
        </ul>
      </div>

      {/* Top goal performers */}
      <div className="card-flat overflow-hidden" data-testid="lb-top-goals">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between bg-emerald-50">
          <div className="flex items-center gap-1.5">
            <Trophy size={13} className="text-emerald-600" />
            <div className="font-display text-sm font-bold text-slate-900">Top performers</div>
          </div>
          <span className="text-[10px] text-slate-500">avg score</span>
        </div>
        <ul className="px-3 py-2">
          {data.top_goals.length === 0 && <li className="text-xs text-slate-400 px-1 py-3">No performance reviews yet.</li>}
          {data.top_goals.map((u, i) => (
            <Row key={u.user_id} avatar={u.avatar} name={u.name} title={u.title}
                 right={`${u.score}${u.metric === "goal_progress" ? "%" : ""}`} rightTone={["green", "emerald", "teal"][i] || "slate"} testid={`lb-goals-${i}`} />
          ))}
        </ul>
      </div>
    </div>
  );
}
